import { Stack, StackProps } from '@mui/material'
import {
  createContext,
  PropsWithChildren,
  ReactNode,
  useContext,
  useMemo,
  useState,
} from 'react'
import { createPortal } from 'react-dom'

type TCampaignEditTabActionsSlotContext = {
  setSlotElement: (element: HTMLElement | null) => void
  slotElement: HTMLElement | null
}

const CampaignEditTabActionsSlotContext =
  createContext<TCampaignEditTabActionsSlotContext | null>(null)

export function CampaignEditTabActionsSlotProvider({
  children = null,
}: PropsWithChildren) {
  const [slotElement, setSlotElement] = useState<HTMLElement | null>(null)
  const value = useMemo(
    () => ({
      slotElement,
      setSlotElement,
    }),
    [slotElement],
  )

  return (
    <CampaignEditTabActionsSlotContext.Provider value={value}>
      {children}
    </CampaignEditTabActionsSlotContext.Provider>
  )
}

function useCampaignEditTabActionsSlot() {
  const context = useContext(CampaignEditTabActionsSlotContext)
  if (!context) {
    throw new Error(
      'Campaign edit tab actions slot must be used within CampaignEditTabActionsSlotProvider',
    )
  }
  return context
}

export function CampaignEditTabActionsSlot({
  sx,
  ...props
}: Omit<StackProps, 'ref' | 'children'>) {
  const { setSlotElement } = useCampaignEditTabActionsSlot()

  return (
    <Stack
      direction="row"
      alignItems="center"
      gap={1}
      flexShrink={0}
      ref={setSlotElement}
      sx={sx}
      {...props}
    />
  )
}

export function CampaignEditTabActionsPortal({
  children = null,
}: {
  children?: ReactNode
}) {
  const context = useContext(CampaignEditTabActionsSlotContext)
  if (!context?.slotElement) {
    return null
  }

  return createPortal(children, context.slotElement)
}
